import React, { useState, useEffect } from 'react';
import './OrgManagement.css';
import { supabase } from '../supabaseClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTimes, faCheckCircle, faBan, faShieldAlt, faEnvelope, faChevronDown, faCopy } from '@fortawesome/free-solid-svg-icons';
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, logOut } from "../firebase";

const OrgManagement = () => {
    const USERS_PER_PAGE = 10;

    // State
    const [orgs, setOrgs] = useState([]);
    const [tools, setTools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editOrgId, setEditOrgId] = useState(null);
    const [formStep, setFormStep] = useState(1); // 1 = Basic Info, 2 = Login & Remaining Details
    const [submitting, setSubmitting] = useState(false);
    const [orgUsers, setOrgUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersError, setUsersError] = useState('');
    const [addUserEmail, setAddUserEmail] = useState('');
    const [addingUser, setAddingUser] = useState(false);
    const [addingUserTempPassword, setAddingUserTempPassword] = useState(false);
    const [tempPasswordModal, setTempPasswordModal] = useState({ open: false, email: '', tempPassword: '' });
    const [copyToast, setCopyToast] = useState('');
    const [usersPage, setUsersPage] = useState(1);
    const [resetPasswordLoadingByEmail, setResetPasswordLoadingByEmail] = useState({});
    const [user] = useAuthState(auth);
    // Form State
    const defaultForm = {
        // Basic Info
        orgName: '',
        orgDescription: '',
        maxUsers: '',
        maxImages: '',
        domainList: '',           // comma-separated, stored as jsonb array
        storageBuckets: '',       // comma-separated, stored as jsonb array
        subscriptionType: 'Free Trial',
        dataLifecycle: 'Not Required',
        status: 'active',
        toolsAccess: [],          // array of tool ids

        // Login type
        ssoEnabled: false,

        // SSO fields (only active when ssoEnabled = true)
        ssoProvider: 'fairpattern-app',
        keycloak_client_id: 'fairpattern-app',

        // Email/Password fields (active when ssoEnabled = false)
        adminMail: '',
        adminName: '',
    };

    const [formData, setFormData] = useState(defaultForm);

    // Fetch Orgs & Tools on mount
    useEffect(() => {
        fetchOrgs();
        fetchTools();
    }, []);

    const fetchOrgs = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('organizations')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setOrgs(data || []);
        } catch (error) {
            console.error('Error fetching organizations:', error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchTools = async () => {
        try {
            const { data, error } = await supabase
                .from('tools')
                .select('id, application_name')
                .order('application_name', { ascending: true });

            if (error) throw error;
            setTools(data || []);
        } catch (error) {
            console.error('Error fetching tools:', error.message);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === 'checkbox') {
            if (name === 'ssoEnabled') {
                setFormData(prev => ({
                    ...prev,
                    ssoEnabled: checked,
                    // Reset dependent fields on toggle
                    adminMail: '',
                    adminName: '',
                }));
                return;
            }
        }

        // Numeric validation
        if (name === 'maxUsers') {
            if (value === '' || /^\d+$/.test(value)) {
                setFormData(prev => ({ ...prev, [name]: value }));
            }
            return;
        }

        if (name === 'maxImages') {
            if (value === '' || /^\d+$/.test(value)) {
                setFormData(prev => ({ ...prev, [name]: value }));
            }
            return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleToolToggle = (toolId) => {
        setFormData(prev => {
            const current = prev.toolsAccess;
            if (current.includes(toolId)) {
                return { ...prev, toolsAccess: current.filter(id => id !== toolId) };
            } else {
                return { ...prev, toolsAccess: [...current, toolId] };
            }
        });
    };

    const resetForm = () => {
        setFormData(defaultForm);
        setIsEditing(false);
        setEditOrgId(null);
        setFormStep(1);
        setOrgUsers([]);
        setUsersLoading(false);
        setUsersError('');
        setAddUserEmail('');
        setAddingUser(false);
        setAddingUserTempPassword(false);
        setTempPasswordModal({ open: false, email: '', tempPassword: '' });
        setCopyToast('');
        setUsersPage(1);
        setResetPasswordLoadingByEmail({});
    };

    const normalizeUsersResponse = (responseData) => {
        if (Array.isArray(responseData)) return responseData;
        if (Array.isArray(responseData?.users)) return responseData.users;
        if (Array.isArray(responseData?.data)) return responseData.data;
        if (Array.isArray(responseData?.result)) return responseData.result;
        if (Array.isArray(responseData?.items)) return responseData.items;
        return [];
    };

    const fetchUsersFromAuthApi = async (baseUrl, orgId) => {
        const response = await fetch(`${baseUrl}/get-users-by-org-id`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ org_id: orgId }),
        });

        if (!response.ok) {
            let message = `Request failed (${response.status})`;
            try {
                const errorData = await response.json();
                message = errorData?.detail || errorData?.message || message;
            } catch {
                // ignore parse error
            }
            throw new Error(message);
        }

        let data = [];
        try {
            data = await response.json();
        } catch {
            data = [];
        }

        return normalizeUsersResponse(data);
    };

    const fetchOrganizationUsers = async (orgId) => {
        if (!orgId) return;

        const baseUrl = process.env.REACT_APP_AUTH_API_BASE_URL;
        if (!baseUrl) {
            setUsersError('Missing REACT_APP_AUTH_API_BASE_URL');
            return;
        }

        setUsersLoading(true);
        setUsersError('');
        try {
            const users = await fetchUsersFromAuthApi(baseUrl, orgId);
            setOrgUsers(users);
            setUsersPage(1);
        } catch (error) {
            console.error('Error fetching organization users:', error.message);
            setUsersError(error.message || 'Failed to fetch organization users');
            setOrgUsers([]);
            setUsersPage(1);
        } finally {
            setUsersLoading(false);
        }
    };

    const handleAddUserToOrganization = async () => {
        if (!editOrgId) {
            alert('Please select an organization first.');
            return;
        }

        const email = addUserEmail.trim();
        if (!email) {
            alert('Please enter user email.');
            return;
        }

        const baseUrl = process.env.REACT_APP_AUTH_API_BASE_URL;
        if (!baseUrl) {
            alert('Missing REACT_APP_AUTH_API_BASE_URL');
            return;
        }

        setAddingUser(true);
        try {
            const response = await fetch(`${baseUrl}/add-user-to-organization`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    org_id: editOrgId,
                    email,
                    invited_by_email: user.email
                }),
            });

            if (!response.ok) {
                let message = 'Failed to add user to organization';
                try {
                    const errorData = await response.json();
                    message = errorData?.detail || errorData?.message || message;
                } catch {
                    // ignore parse error
                }
                throw new Error(message);
            }

            setAddUserEmail('');
            await fetchOrganizationUsers(editOrgId);
            alert('User added to organization successfully.');
        } catch (error) {
            console.error('Error adding user to organization:', error.message);
            alert(error.message || 'Failed to add user to organization');
        } finally {
            setAddingUser(false);
        }
    };

    const closeTempPasswordModal = () => {
        setTempPasswordModal({ open: false, email: '', tempPassword: '' });
        setCopyToast('');
    };

    const copyToClipboard = async (label, text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopyToast(`${label} copied`);
            setTimeout(() => setCopyToast(''), 2000);
        } catch (err) {
            console.error('Clipboard error:', err);
            alert('Could not copy. Copy manually.');
        }
    };

    const handleAddUserWithTempPassword = async () => {
        if (!editOrgId) {
            alert('Please select an organization first.');
            return;
        }

        const email = addUserEmail.trim();
        if (!email) {
            alert('Please enter user email.');
            return;
        }

        if (!user?.email) {
            alert('You must be signed in with an email to use temporary password invite.');
            return;
        }

        const baseUrl = process.env.REACT_APP_AUTH_API_BASE_URL;
        if (!baseUrl) {
            alert('Missing REACT_APP_AUTH_API_BASE_URL');
            return;
        }

        setAddingUserTempPassword(true);
        try {
            const response = await fetch(`${baseUrl}/auth/login-with-temp-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    invited_by_email: user.email,
                    org_id: editOrgId,
                }),
            });

            if (!response.ok) {
                let message = 'Failed to create user with temporary password';
                try {
                    const errorData = await response.json();
                    message = errorData?.detail || errorData?.message || message;
                } catch {
                    // ignore parse error
                }
                throw new Error(message);
            }

            let data = {};
            try {
                data = await response.json();
            } catch {
                data = {};
            }

            const resultEmail = data.email ?? email;
            const tempPassword = data.temp_password ?? data.tempPassword ?? '';
            setTempPasswordModal({
                open: true,
                email: resultEmail,
                tempPassword,
            });
            setAddUserEmail('');
            await fetchOrganizationUsers(editOrgId);
        } catch (error) {
            console.error('Error adding user with temporary password:', error.message);
            alert(error.message || 'Failed to add user with temporary password');
        } finally {
            setAddingUserTempPassword(false);
        }
    };

    const handleSendResetPasswordLink = async (email) => {
        const finalEmail = String(email || '').trim();
        if (!finalEmail) {
            alert('Email not available for this user.');
            return;
        }

        const baseUrl = process.env.REACT_APP_AUTH_API_BASE_URL;
        if (!baseUrl) {
            alert('Missing REACT_APP_AUTH_API_BASE_URL');
            return;
        }

        setResetPasswordLoadingByEmail(prev => ({ ...prev, [finalEmail]: true }));
        try {
            const response = await fetch(`${baseUrl}/auth/forgot-password/send-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: finalEmail }),
            });

            if (!response.ok) {
                let message = 'Failed to send reset password link';
                try {
                    const errorData = await response.json();
                    message = errorData?.detail || errorData?.message || message;
                } catch {
                    // ignore parse error
                }
                throw new Error(message);
            }

            alert(`Reset password link sent to ${finalEmail}`);
        } catch (error) {
            console.error('Error sending reset password link:', error.message);
            alert(error.message || 'Failed to send reset password link');
        } finally {
            setResetPasswordLoadingByEmail(prev => ({ ...prev, [finalEmail]: false }));
        }
    };

    const handleEdit = (org) => {
        setFormData({
            orgName: org.org_name || '',
            orgDescription: org.org_description || '',
            maxUsers: org.max_users ?? '',
            maxImages: org.max_images ?? '',
            domainList: org.domain_list ? (Array.isArray(org.domain_list) ? org.domain_list.join(', ') : '') : '',
            storageBuckets: org.storage_buckets ? (Array.isArray(org.storage_buckets) ? org.storage_buckets.join(', ') : '') : '',
            subscriptionType: org.subscription_type || 'Free Trial',
            dataLifecycle: org.data_life_cycle || 'Not Required',
            status: org.status || 'active',
            toolsAccess: Array.isArray(org.tools_access) ? org.tools_access : [],
            ssoEnabled: org.sso_enabled || false,
            ssoProvider: org.sso_provider || 'fairpattern-app',
            keycloak_client_id: org.keycloak_client_id || 'fairpattern-app',
            adminMail: org.admin_mail || '',
            adminName: org.admin_name || '',
        });
        setEditOrgId(org.org_id);
        setIsEditing(true);
        setFormStep(1);
        setShowForm(true);
        fetchOrganizationUsers(org.org_id);
    };

    const handleNextStep = (e) => {
        e.preventDefault();
        if (!formData.orgName.trim()) {
            alert('Organization name is required.');
            return;
        }
        setFormStep(2);
    };

    const inviteAdminUser = async (orgId, adminEmail, adminName) => {
        const invitePayload = {
            email: adminEmail,
            org_id: orgId,
            name: adminName,
            role: 'admin',
            status: 'active'
        };

        const response = await fetch(`${process.env.REACT_APP_AUTH_API_BASE_URL}/invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(invitePayload),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to send invite');
        }

        return await response.json();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        // Validation
        if (!formData.ssoEnabled && !formData.adminMail.trim()) {
            alert('Admin email is required for Email/Password login.');
            setSubmitting(false);
            return;
        }
        if (!formData.ssoEnabled && !formData.adminName.trim()) {
            alert('Admin name is required for Email/Password login.');
            setSubmitting(false);
            return;
        }
        if (formData.ssoEnabled && !formData.adminMail.trim()) {
            alert('Admin email is required even for SSO (used as identifier).');
            setSubmitting(false);
            return;
        }

        const bucketsArray = formData.storageBuckets.split(',').map(b => b.trim()).filter(Boolean);
        const domainArray = formData.domainList.split(',').map(d => d.trim()).filter(Boolean);
        const maxUsers = formData.maxUsers !== '' ? parseInt(formData.maxUsers, 10) : null;
        const maxImages = formData.maxImages !== '' ? parseInt(formData.maxImages, 10) : null;

        const payload = {
            org_name: formData.orgName.trim(),
            org_description: formData.orgDescription.trim() || null,
            max_users: maxUsers,
            max_images: maxImages,
            domain_list: domainArray,
            storage_buckets: bucketsArray,
            subscription_type: formData.subscriptionType,
            data_life_cycle: formData.dataLifecycle,
            status: formData.status,
            tools_access: formData.toolsAccess,
            admin_mail: formData.adminMail.trim(),
            admin_name: formData.ssoEnabled ? null : formData.adminName.trim(),
            sso_enabled: formData.ssoEnabled,
            sso_provider: formData.ssoEnabled ? 'fairpattern-app' : null,
            keycloak_client_id: formData.ssoEnabled ? 'fairpattern-app' : null,
            updated_at: new Date().toISOString(),
        };

        try {
            let orgId = editOrgId;

            if (isEditing) {
                const { error } = await supabase
                    .from('organizations')
                    .update(payload)
                    .eq('org_id', editOrgId);

                if (error) throw error;

                // Send invite only for Email/Password login
                if (!formData.ssoEnabled) {
                    try {
                        await inviteAdminUser(orgId, formData.adminMail, formData.adminName);
                        alert('Organization updated and admin invite sent successfully!');
                    } catch (inviteError) {
                        alert('Organization updated, but failed to send admin invite: ' + inviteError.message);
                    }
                } else {
                    alert('Organization updated successfully! (SSO login – no invite sent)');
                }

            } else {
                const { data, error } = await supabase
                    .from('organizations')
                    .insert([payload])
                    .select();

                if (error) throw error;

                if (data && data.length > 0) {
                    orgId = data[0].org_id;

                    // Send invite only for Email/Password login
                    if (!formData.ssoEnabled) {
                        try {
                            await inviteAdminUser(orgId, formData.adminMail, formData.adminName);
                            alert('Organization created and admin invite sent successfully!');
                        } catch (inviteError) {
                            alert('Organization created, but failed to send admin invite: ' + inviteError.message);
                        }
                    } else {
                        alert('Organization created successfully! (SSO login – no invite sent)');
                    }
                }
            }

            fetchOrgs();
            setShowForm(false);
            resetForm();

        } catch (error) {
            console.error('Error saving organization:', error.message);
            alert('Error saving organization: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const getToolName = (toolId) => {
        const tool = tools.find(t => t.id === toolId);
        return tool ? tool.application_name : toolId;
    };

    const formatInvitedAt = (value) => {
        if (!value) return '—';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '—';
        return date.toLocaleString();
    };

    const totalUsersPages = Math.max(1, Math.ceil(orgUsers.length / USERS_PER_PAGE));
    const usersStartIndex = (usersPage - 1) * USERS_PER_PAGE;
    const paginatedUsers = orgUsers.slice(usersStartIndex, usersStartIndex + USERS_PER_PAGE);

    return (
        <div className="org-management-container">
            <div className="org-management-header">
                <div className="header-content">
                    <h1>Organization Management</h1>
                    <p>Manage your organizations, users, and resources efficiently.</p>
                </div>
                <button
                    className={`add-org-btn ${showForm ? 'active' : ''}`}
                    onClick={() => {
                        if (showForm) resetForm();
                        setShowForm(!showForm);
                    }}
                >
                    <FontAwesomeIcon icon={showForm ? faTimes : faPlus} />
                    {showForm ? ' Close Form' : ' Add Organization'}
                </button>
            </div>

            {/* Form Section */}
            <div className={`org-form-wrapper ${showForm ? 'open' : ''}`}>
                <section className="org-form-section">
                    <div className="form-header-row">
                        <h2>{isEditing ? 'Edit Organization' : 'Create New Organization'}</h2>
                        {/* Step indicator */}
                        <div className="step-indicator">
                            <div className={`step-dot ${formStep >= 1 ? 'active' : ''}`}>
                                <span>1</span>
                                <label>Basic Info</label>
                            </div>
                            <div className="step-line" />
                            <div className={`step-dot ${formStep >= 2 ? 'active' : ''}`}>
                                <span>2</span>
                                <label>Login & Details</label>
                            </div>
                        </div>
                    </div>

                    {/* ── STEP 1: BASIC INFO ── */}
                    {formStep === 1 && (
                        <form className="org-form" onSubmit={handleNextStep}>
                            <div className="form-section-label">Basic Information</div>

                            <div className="form-group">
                                <label htmlFor="orgName">Organization Name <span className="required">*</span></label>
                                <input
                                    type="text"
                                    id="orgName"
                                    name="orgName"
                                    value={formData.orgName}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Enter organization name"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="orgDescription">Description</label>
                                <textarea
                                    id="orgDescription"
                                    name="orgDescription"
                                    value={formData.orgDescription}
                                    onChange={handleInputChange}
                                    placeholder="Short description of the organization"
                                    rows={3}
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="maxUsers">Max Users <span className="optional">(optional)</span></label>
                                    <input
                                        type="number"
                                        id="maxUsers"
                                        name="maxUsers"
                                        min="1"
                                        value={formData.maxUsers}
                                        onChange={handleInputChange}
                                        placeholder="e.g. 50"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="maxImages">Max Images <span className="optional">(optional)</span></label>
                                    <input
                                        type="number"
                                        id="maxImages"
                                        name="maxImages"
                                        min="1"
                                        value={formData.maxImages}
                                        onChange={handleInputChange}
                                        placeholder="e.g. 500"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="domainList">Allowed Domains <span className="optional">(comma-separated)</span></label>
                                <input
                                    type="text"
                                    id="domainList"
                                    name="domainList"
                                    value={formData.domainList}
                                    onChange={handleInputChange}
                                    placeholder="company.com, partner.org"
                                />
                                <span className="field-hint">Only users from these domains can be invited.</span>
                            </div>

                            <div className="form-group">
                                <label htmlFor="storageBuckets">Storage Buckets <span className="optional">(comma-separated)</span></label>
                                <input
                                    type="text"
                                    id="storageBuckets"
                                    name="storageBuckets"
                                    value={formData.storageBuckets}
                                    onChange={handleInputChange}
                                    placeholder="bucket1, bucket2"
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="subscriptionType">Subscription Type</label>
                                    <div className="select-wrapper">
                                        <select
                                            id="subscriptionType"
                                            name="subscriptionType"
                                            value={formData.subscriptionType}
                                            onChange={handleInputChange}
                                        >
                                            <option value="Free Trial">Free Trial</option>
                                            <option value="Professional">Professional</option>
                                            <option value="Enterprise">Enterprise</option>
                                        </select>
                                        <FontAwesomeIcon icon={faChevronDown} className="select-icon" />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="dataLifecycle">Data Life Cycle</label>
                                    <div className="select-wrapper">
                                        <select
                                            id="dataLifecycle"
                                            name="dataLifecycle"
                                            value={formData.dataLifecycle}
                                            onChange={handleInputChange}
                                        >
                                            <option value="1 day">1 day</option>
                                            <option value="30 days">30 days</option>
                                            <option value="60 days">60 days</option>
                                            <option value="90 days">90 days</option>
                                            <option value="Not Required">Not Required</option>
                                        </select>
                                        <FontAwesomeIcon icon={faChevronDown} className="select-icon" />
                                    </div>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="status">Status</label>
                                    <div className="select-wrapper">
                                        <select
                                            id="status"
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                        <FontAwesomeIcon icon={faChevronDown} className="select-icon" />
                                    </div>
                                </div>
                            </div>

                            {/* Tools Access */}
                            <div className="form-group">
                                <label>Tools Access</label>
                                <span className="field-hint">Select tools this organization can access.</span>
                                {tools.length === 0 ? (
                                    <p className="no-tools-msg">No tools available.</p>
                                ) : (
                                    <div className="tools-grid">
                                        {tools.map(tool => (
                                            <label key={tool.id} className={`tool-chip ${formData.toolsAccess.includes(tool.id) ? 'selected' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.toolsAccess.includes(tool.id)}
                                                    onChange={() => handleToolToggle(tool.id)}
                                                />
                                                {tool.application_name}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="form-actions">
                                <button type="submit" className="submit-btn next-btn">
                                    Next: Login & Details →
                                </button>
                            </div>
                        </form>
                    )}

                    {/* ── STEP 2: LOGIN TYPE + REMAINING DETAILS ── */}
                    {formStep === 2 && (
                        <form className="org-form" onSubmit={handleSubmit}>
                            <div className="form-section-label">Login Configuration</div>

                            {/* SSO Toggle */}
                            <div className="login-type-toggle">
                                <button
                                    type="button"
                                    className={`login-type-btn ${!formData.ssoEnabled ? 'active-login' : ''}`}
                                    onClick={() => setFormData(prev => ({ ...prev, ssoEnabled: false, adminMail: '', adminName: '' }))}
                                >
                                    <FontAwesomeIcon icon={faEnvelope} />
                                    <span>Email / Password</span>
                                </button>
                                <button
                                    type="button"
                                    className={`login-type-btn ${formData.ssoEnabled ? 'active-login' : ''}`}
                                    onClick={() => setFormData(prev => ({ ...prev, ssoEnabled: true, adminMail: '', adminName: '' }))}
                                >
                                    <FontAwesomeIcon icon={faShieldAlt} />
                                    <span>SSO (Single Sign-On)</span>
                                </button>
                            </div>

                            {/* Email/Password Fields */}
                            {!formData.ssoEnabled && (
                                <div className="login-fields-panel email-panel">
                                    <div className="panel-info">
                                        An invitation email will be sent to the admin to set up their account.
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="adminMail">Admin Email <span className="required">*</span></label>
                                            <input
                                                type="email"
                                                id="adminMail"
                                                name="adminMail"
                                                value={formData.adminMail}
                                                onChange={handleInputChange}
                                                required={!formData.ssoEnabled}
                                                placeholder="admin@company.com"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="adminName">Admin Name <span className="required">*</span></label>
                                            <input
                                                type="text"
                                                id="adminName"
                                                name="adminName"
                                                value={formData.adminName}
                                                onChange={handleInputChange}
                                                required={!formData.ssoEnabled}
                                                placeholder="Full name"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* SSO Fields */}
                            {formData.ssoEnabled && (
                                <div className="login-fields-panel sso-panel">
                                    <div className="panel-info sso-info">
                                        SSO is managed via <strong>fairpattern-app</strong>. No invitation email will be sent. Admin email is recorded as the identifier only.
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="adminMailSso">Admin Email <span className="required">*</span></label>
                                        <input
                                            type="email"
                                            id="adminMailSso"
                                            name="adminMail"
                                            value={formData.adminMail}
                                            onChange={handleInputChange}
                                            required={formData.ssoEnabled}
                                            placeholder="admin@company.com"
                                        />
                                        <span className="field-hint">Stored as identifier only. No invite will be sent.</span>
                                    </div>
                                    <div className="form-group">
                                        <label>SSO Provider</label>
                                        <input
                                            type="text"
                                            value="fairpattern-app"
                                            disabled
                                            className="disabled-input"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="form-actions step2-actions">
                                <button
                                    type="button"
                                    className="back-btn"
                                    onClick={() => setFormStep(1)}
                                >
                                    ← Back
                                </button>
                                <button type="submit" className="submit-btn" disabled={submitting}>
                                    <FontAwesomeIcon icon={isEditing ? faCheckCircle : faPlus} />
                                    {submitting
                                        ? ' Saving...'
                                        : isEditing
                                            ? ' Update Organization'
                                            : ' Create Organization'
                                    }
                                </button>
                            </div>

                        </form>
                    )}

                    {isEditing && showForm && (
                        <div className="form-group org-users-section">
                            <label>Organization Users</label>
                            <span className="field-hint">Add users and view all users in this organization.</span>

                            <div className="org-users-row">
                                <div className="form-group org-users-email-group">
                                    <label htmlFor="addUserEmail">Email</label>
                                    <input
                                        type="email"
                                        id="addUserEmail"
                                        value={addUserEmail}
                                        onChange={(e) => setAddUserEmail(e.target.value)}
                                        placeholder="user@company.com"
                                        className="org-users-email-input"
                                    />
                                    <div className="org-users-buttons-row">
                                        <button
                                            type="button"
                                            className="submit-btn add-user-btn"
                                            onClick={handleAddUserToOrganization}
                                            disabled={addingUser || addingUserTempPassword}
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                            {addingUser ? ' Adding...' : ' Add User by invitation email'}
                                        </button>
                                        <button
                                            type="button"
                                            className="submit-btn add-user-btn add-user-temp-btn"
                                            onClick={handleAddUserWithTempPassword}
                                            disabled={addingUser || addingUserTempPassword}
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                            {addingUserTempPassword ? ' Working...' : ' Add User by Temporary Password'}
                                        </button>
                                    </div>
                                    <span className="field-hint">
                                        Add User by invitation email sends an invitation email. Add User by temporary password creates credentials and shows them in a dialog (copy to share securely).
                                    </span>
                                </div>
                            </div>

                            {usersLoading ? (
                                <p style={{ marginTop: '0.75rem' }}>Loading users...</p>
                            ) : usersError ? (
                                <p style={{ marginTop: '0.75rem', color: '#d93025' }}>{usersError}</p>
                            ) : orgUsers.length > 0 ? (
                                <>
                                    <div className="users-table-wrap">
                                        <table className="users-table">
                                            <thead>
                                                <tr>
                                                    <th>Email</th>
                                                    <th>Invited By</th>
                                                    <th>Invited At</th>
                                                    <th>Forgot Password</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedUsers.map((user, index) => (
                                                    <tr key={user.id || user.user_id || user.email || `${usersPage}-${index}`}>
                                                        <td>{user.email || user.admin_mail || user.user_email || user.name || 'Unknown user'}</td>
                                                        <td>{user.invited_by || '—'}</td>
                                                        <td>{formatInvitedAt(user.created_at)}</td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="users-reset-btn"
                                                                onClick={() =>
                                                                    handleSendResetPasswordLink(
                                                                        user.email || user.admin_mail || user.user_email || ''
                                                                    )
                                                                }
                                                                disabled={resetPasswordLoadingByEmail[user.email || user.admin_mail || user.user_email || '']}
                                                            >
                                                                {resetPasswordLoadingByEmail[user.email || user.admin_mail || user.user_email || '']
                                                                    ? 'Sending...'
                                                                    : 'Send Link'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="users-pagination">
                                        <button
                                            type="button"
                                            className="users-page-btn"
                                            onClick={() => setUsersPage(prev => Math.max(1, prev - 1))}
                                            disabled={usersPage === 1}
                                        >
                                            Previous
                                        </button>
                                        <span className="users-page-info">
                                            Page {usersPage} of {totalUsersPages}
                                        </span>
                                        <button
                                            type="button"
                                            className="users-page-btn"
                                            onClick={() => setUsersPage(prev => Math.min(totalUsersPages, prev + 1))}
                                            disabled={usersPage === totalUsersPages}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <p style={{ marginTop: '0.75rem' }}>No users found for this organization.</p>
                            )}
                        </div>
                    )}
                </section>
            </div>

            {/* Table Section */}
            <section className="org-table-section">
                <h2>Organization Dashboard</h2>
                {loading ? (
                    <p>Loading organizations...</p>
                ) : (
                    <div className="table-responsive">
                        <table className="org-table">
                            <thead>
                                <tr>
                                    <th>Org Name</th>
                                    <th>Subscription</th>
                                    <th>Login</th>
                                    <th>Users (Active/Max)</th>
                                    <th>Images (Used/Max)</th>
                                    <th>Tools</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orgs.length > 0 ? (
                                    orgs.map((org) => (
                                        <tr key={org.org_id}>
                                            <td className="org-name-cell">
                                                <strong>{org.org_name}</strong>
                                                {org.admin_mail && <div className="org-email-sub">{org.admin_mail}</div>}
                                            </td>
                                            <td>
                                                <span className={`sub-badge sub-${(org.subscription_type || '').toLowerCase().replace(/\s+/g, '-')}`}>
                                                    {org.subscription_type || '—'}
                                                </span>
                                            </td>
                                            <td>
                                                {org.sso_enabled ? (
                                                    <span className="login-badge sso-badge">
                                                        <FontAwesomeIcon icon={faShieldAlt} /> SSO
                                                    </span>
                                                ) : (
                                                    <span className="login-badge email-badge">
                                                        <FontAwesomeIcon icon={faEnvelope} /> Email
                                                    </span>
                                                )}
                                            </td>
                                            <td>{org.active_users ?? 0} / {org.max_users ?? '∞'}</td>
                                            <td>{org.images_processed ?? 0} / {org.max_images ?? '∞'}</td>
                                            <td>
                                                {Array.isArray(org.tools_access) && org.tools_access.length > 0 ? (
                                                    <div className="tools-tag-list">
                                                        {org.tools_access.map(tid => (
                                                            <span key={tid} className="tool-tag">{getToolName(tid)}</span>
                                                        ))}
                                                    </div>
                                                ) : <span className="no-tools">—</span>}
                                            </td>
                                            <td>
                                                <span className={`status-badge ${org.status === 'active' ? 'status-active' : 'status-inactive'}`}>
                                                    {org.status === 'active' ? (
                                                        <><FontAwesomeIcon icon={faCheckCircle} /> Active</>
                                                    ) : (
                                                        <><FontAwesomeIcon icon={faBan} /> Inactive</>
                                                    )}
                                                </span>
                                            </td>
                                            <td>
                                                <button className="icon-btn edit-btn" onClick={() => handleEdit(org)} title="Edit">
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                                            No organizations found. Click "+" to create one.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {tempPasswordModal.open && (
                <div
                    className="temp-password-modal-backdrop"
                    role="presentation"
                    onClick={closeTempPasswordModal}
                >
                    <div
                        className="temp-password-modal"
                        role="dialog"
                        aria-labelledby="temp-password-modal-title"
                        aria-modal="true"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="temp-password-modal-header">
                            <h3 id="temp-password-modal-title">Temporary password created</h3>
                            <button
                                type="button"
                                className="temp-password-modal-close"
                                onClick={closeTempPasswordModal}
                                aria-label="Close"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <p className="temp-password-modal-lead">
                            Share these credentials with the user securely. This dialog does not send email.
                        </p>
                        <div className="temp-password-field">
                            <label htmlFor="temp-pw-email">Email</label>
                            <div className="temp-password-field-row">
                                <input id="temp-pw-email" readOnly value={tempPasswordModal.email} />
                                <button
                                    type="button"
                                    className="temp-password-copy-btn"
                                    onClick={() => copyToClipboard('Email', tempPasswordModal.email)}
                                >
                                    <FontAwesomeIcon icon={faCopy} /> Copy
                                </button>
                            </div>
                        </div>
                        <div className="temp-password-field">
                            <label htmlFor="temp-pw-value">Temporary password</label>
                            <div className="temp-password-field-row">
                                <input id="temp-pw-value" readOnly value={tempPasswordModal.tempPassword} />
                                <button
                                    type="button"
                                    className="temp-password-copy-btn"
                                    onClick={() => copyToClipboard('Password', tempPasswordModal.tempPassword)}
                                >
                                    <FontAwesomeIcon icon={faCopy} /> Copy
                                </button>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="temp-password-copy-all-btn"
                            onClick={() =>
                                copyToClipboard(
                                    'Credentials',
                                    `Email: ${tempPasswordModal.email}\nTemporary password: ${tempPasswordModal.tempPassword}`
                                )
                            }
                        >
                            <FontAwesomeIcon icon={faCopy} /> Copy email + password
                        </button>
                        {copyToast && <p className="temp-password-copy-toast">{copyToast}</p>}
                        <div className="temp-password-modal-footer">
                            <button type="button" className="submit-btn" onClick={closeTempPasswordModal}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrgManagement;
